import { CheckCircleIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  LinearProgress,
  Modal,
  ModalClose,
  ModalDialog,
} from '@mui/joy';
import { SelectTrigger } from '@radix-ui/react-select';

import { Dispatch, SetStateAction, useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/Select/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/Table';
import { Button, Tooltip } from '@/design-system';
import { useCurrentTier } from '@/hooks/useCurrentTier';
import {
  InstanceFlags,
  useInstanceFlagOrDefault,
} from '@/hooks/useInstanceFlag';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import {
  useCurrentOrganization,
  useInviteOrgMembersMutation,
  useOrgWorkspaces,
  useRoles,
} from '@/hooks/useSwr';
import FileDownloadIcon from '@/icons/FileDownloadIcon.svg?react';
import InviteUserIcon from '@/icons/InviteUserIcon.svg?react';
import {
  ChangePaymentPlanTierSchema,
  IdentitySchema,
  TenantSchema,
} from '@/types/schema';
import { mixedLoginMethods } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import {
  MemberGroupType,
  MemberStatus,
} from '../types/organizationMembers.type';
import { PAYMENT_PLAN_PRICING_MAP } from '../utils/constants';
import { useMembers } from '../utils/useMembers';
import { BulkEmailAddressInput } from './BulkEmailAddressInput';
import { UpsellBadge } from './UpsellBadge';

export function WorkspaceSelect(props: {
  disabled?: boolean;
  value: TenantSchema[];
  onChange: Dispatch<SetStateAction<TenantSchema[]>>;
}) {
  const { data, isLoading } = useOrgWorkspaces();
  if (isLoading) return null;
  return (
    <Popover>
      <PopoverTrigger
        disabled={props.disabled}
        className="flex flex-wrap gap-2 rounded-md border border-secondary p-2 px-2.5 text-left disabled:bg-secondary disabled:text-tertiary data-[placeholder]:text-tertiary"
      >
        {props.value.length > 0 ? (
          props.value.map((v) => (
            <span
              className="flex items-center gap-1 rounded-md border border-secondary px-2 "
              key={v.id}
            >
              <span>{v.display_name}</span>
            </span>
          ))
        ) : (
          <span className="text-tertiary">Select workspaces...</span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[30vh] overflow-y-auto p-0"
        style={{
          width: 'var(--radix-popper-anchor-width)',
        }}
      >
        <Command>
          <CommandInput
            className="m-2 mb-0 px-2"
            placeholder="Search workspaces..."
          />
          <CommandList>
            <CommandGroup>
              {data?.map((workspace) => {
                const selected = !!props.value.find(
                  (v) => v.id === workspace.id
                );
                return (
                  <CommandItem
                    className="relative pl-8"
                    key={workspace.id}
                    onSelect={() => {
                      props.onChange((oldValue) => {
                        if (!oldValue) return [workspace];
                        const existingSelection = oldValue.findIndex(
                          (v) => v.id === workspace.id
                        );
                        if (existingSelection > -1) {
                          return oldValue.filter(
                            (_, i) => i !== existingSelection
                          );
                        }
                        return [...oldValue, workspace];
                      });
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {selected && (
                        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                          <CheckIcon className="h-4 w-4" />
                        </span>
                      )}
                      {workspace.display_name}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandEmpty>No workspaces found</CommandEmpty>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function RoleSelect(props: {
  value: string | undefined;
  onChange: (value: string) => void;
  disabled?: boolean;
  groupType: 'organization' | 'workspace';
  className?: string;
}) {
  const rolesQuery = useRoles(
    props.groupType === 'organization' ? 'organization' : 'workspace'
  );
  const roles = rolesQuery.data;

  if (rolesQuery.isLoading) return null;
  return (
    <Select
      value={props.value}
      onValueChange={props.onChange}
      disabled={props.disabled}
    >
      <SelectTrigger
        className={cn(
          'rounded-md border border-secondary p-2 px-2.5 text-left disabled:bg-secondary disabled:text-tertiary data-[placeholder]:text-tertiary',
          props.className
        )}
        disabled={props.disabled}
      >
        <SelectValue placeholder="Select role..." />
      </SelectTrigger>
      <SelectContent>
        {roles?.map((role) => {
          return (
            <SelectItem key={role.id} value={role.id}>
              <div className="flex items-center gap-2">{role.display_name}</div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function OrgMemberForm(props: {
  memberCount: number;
  changePlanTier?: ChangePaymentPlanTierSchema;
  onComplete?: (members: IdentitySchema[]) => void;
}) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const inviteOrgMutation = useInviteOrgMembersMutation(
    mixedLoginMethods.basic
  );

  const isPaymentEnabled = useInstanceFlagOrDefault(
    InstanceFlags.payment_enabled
  ) as boolean;
  const { value: isRbacEnabled } = useOrgConfig(OrgConfigs.can_use_rbac);

  let { value: maxSeats } = useOrgConfig(OrgConfigs.max_identities);
  if (typeof maxSeats === 'string') maxSeats = parseInt(maxSeats);
  maxSeats = maxSeats as number;

  const organizationQuery = useCurrentOrganization();
  const { isLegacy, tier, isEnterprise } = useCurrentTier();
  const tierToUse = props.changePlanTier ?? tier;

  const [emails, setEmails] = useState<string[]>([]);
  const [roleId, setRoleId] = useState<string | undefined>(undefined);
  const [workspaceRoleId, setWorkspaceRoleId] = useState<string | undefined>(
    undefined
  );
  const [workspaces, setWorkspaces] = useState<TenantSchema[]>([]);
  const error = validationError ?? inviteOrgMutation.error;

  const orgRolesQuery = useRoles('organization');
  const workspaceRolesQuery = useRoles('workspace');

  const { data } = useOrgWorkspaces();

  const handleEmailChange = (newEmails: string[]) => {
    setValidationError(null);
    setEmails(newEmails);
  };
  const orgAdminRole = orgRolesQuery.data?.find(
    (r) => r.name === 'ORGANIZATION_ADMIN'
  );

  const setOrgRole = (orgRoleId: string) => {
    if (orgAdminRole?.id === orgRoleId) {
      const workspaceAdminRole = workspaceRolesQuery.data?.find(
        (r) => r.name === 'WORKSPACE_ADMIN'
      );
      setWorkspaces(data ?? []);
      if (isRbacEnabled) {
        setWorkspaceRoleId(workspaceAdminRole?.id);
      }
    }
    setRoleId(orgRoleId);
  };

  if (organizationQuery.isLoading) {
    return <LinearProgress />;
  }

  return (
    <form
      className="flex flex-col items-stretch gap-8"
      onSubmit={async (e) => {
        e.preventDefault();
        const nEmails = emails.length;

        const emailSet = new Set();
        const duplicateEmails = emails.filter((email) => {
          if (emailSet.has(email)) return true;
          emailSet.add(email);
          return false;
        });

        if (duplicateEmails.length > 0) {
          setValidationError(
            `Duplicate emails found: ${duplicateEmails.join(', ')}`
          );
          return;
        } else if (nEmails === 0) {
          setValidationError('At least one email is required');
          return;
        } else if (nEmails > maxSeats) {
          setValidationError(`You can invite up to ${maxSeats} members`);
          return;
        } else if (nEmails > 500) {
          setValidationError('You can invite up to 500 members at a time');
          return;
        } else if (workspaces.length === 0) {
          setValidationError('At least one workspace is required');
          return;
        } else {
          setValidationError(null);
        }

        const resp = await inviteOrgMutation.trigger({
          json: emails.map((email) => ({
            email,
            role_id: roleId,
            workspace_ids: workspaces.map((w) => w.id),
            workspace_role_id: workspaceRoleId,
          })),
        });
        if (resp) {
          props.onComplete?.(resp);
        }
      }}
    >
      <div className="flex flex-col items-stretch gap-6">
        <BulkEmailAddressInput
          memberCount={props.memberCount}
          value={emails}
          maxMemberCount={maxSeats}
          onChange={handleEmailChange}
          label="Emails"
        />
        {isPaymentEnabled && !isLegacy && !isEnterprise && (
          <div className="inline-flex flex-col items-start justify-start gap-2">
            <div className="flex flex-col items-start justify-start gap-1.5 self-stretch">
              <FormLabel sx={{ fontSize: '14px' }}>Cost</FormLabel>
              <div className="inline-flex items-start justify-start self-stretch rounded-lg border border-secondary bg-secondary">
                <div className="flex h-11 shrink grow basis-0 items-center justify-start gap-2 py-2.5 pl-3.5">
                  <div className="flex h-6 shrink grow basis-0 items-center justify-start gap-2">
                    <div className="text-base font-normal leading-normal text-tertiary">
                      $
                    </div>
                    <div className="shrink grow basis-0">
                      {emails.length *
                        (tierToUse
                          ? PAYMENT_PLAN_PRICING_MAP[tierToUse].pricePerSeat
                          : PAYMENT_PLAN_PRICING_MAP['plus'].pricePerSeat)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between self-stretch px-3.5 py-2.5">
                  <div className="text-base font-normal leading-normal text-tertiary">
                    / billed monthly
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch text-sm font-normal leading-tight text-tertiary">
              $
              {tierToUse
                ? PAYMENT_PLAN_PRICING_MAP[tierToUse].pricePerSeat
                : PAYMENT_PLAN_PRICING_MAP['plus'].pricePerSeat}{' '}
              per seat per month. Seats are charged at the beginning of each
              month. The first month is prorated, and will be invoiced within a
              few hours.
            </div>
          </div>
        )}
      </div>

      <FormControl>
        <div className="flex flex-col">
          <FormLabel className={'gap-2'}>Organization role</FormLabel>
          <RoleSelect
            value={roleId}
            onChange={setOrgRole}
            groupType="organization"
          />
          {roleId === orgAdminRole?.id && (
            <FormHelperText>
              Organization admins are added to all workspaces as the "Admin"
              role
            </FormHelperText>
          )}
        </div>
      </FormControl>

      <FormControl>
        <div className="flex flex-col">
          <FormLabel>Invite to workspaces</FormLabel>
          <WorkspaceSelect
            disabled={roleId === orgAdminRole?.id}
            value={workspaces}
            onChange={setWorkspaces}
          />
        </div>
      </FormControl>

      <FormControl>
        <div className="flex flex-col">
          <FormLabel>
            Workspace role{' '}
            {!isRbacEnabled && (
              <UpsellBadge
                tooltipMsg={
                  'Role based access control is only available for enterprise customers. Contact our sales team for more'
                }
                className="ml-2"
                tier="ENTERPRISE"
                linkProps={{
                  to: 'https://www.langchain.com/contact-sales',
                  target: '_blank',
                }}
              />
            )}
          </FormLabel>
          {isRbacEnabled && (
            <div className="text-sm">
              Users will be added to the above workspaces with this role
            </div>
          )}
          <RoleSelect
            className="mt-1"
            disabled={roleId === orgAdminRole?.id || !isRbacEnabled}
            value={workspaceRoleId}
            onChange={setWorkspaceRoleId}
            groupType="workspace"
          />
        </div>
      </FormControl>

      {error && <ExpandableErrorAlert error={error} />}

      <div className="flex items-center justify-between">
        <Button
          type="submit"
          loading={inviteOrgMutation.isMutating}
          disabled={emails.length === 0}
        >
          Send invites
        </Button>
      </div>
    </form>
  );
}
const DownloadCSVLink = ({ members }: { members: IdentitySchema[] }) => {
  const generateCSV = () => {
    const headers = ['Email', 'Password'];
    const rows = members.map((member) => `${member.email},${member.password}`);
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadCSV = () => {
    const csv = generateCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'member_credentials.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <button
      type="button"
      onClick={downloadCSV}
      className="text-brand-green-400-600 hover:text-brand-green-400-700 inline-flex items-center gap-2 text-sm font-medium"
    >
      <FileDownloadIcon className="h-5 w-5" />
      Download as CSV
    </button>
  );
};
export function BasicAuthInviteSuccess({
  members,
  close,
  inviteMore,
}: {
  members: IdentitySchema[];
  close: () => void;
  inviteMore: () => void;
}) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2 rounded-md bg-success-secondary p-3 text-success">
        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm font-medium">Invite succeeded.</span>
      </div>
      <div className="mb-4 text-sm text-error" style={{ fontWeight: 'bold' }}>
        Save these passwords. Users need them to login, and this information
        won't be shown again.
      </div>
      <div className="mb-4">
        <DownloadCSVLink members={members} />
      </div>
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50%] pl-5">Email</TableHead>
            <TableHead className="w-[50%] pl-5">Password</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.id}>
              <TableCell className="w-[50%] pl-5">
                <div className="flex items-center gap-2">
                  <span className="max-w-[270px] overflow-hidden text-ellipsis">
                    {member.email}
                  </span>
                </div>
              </TableCell>
              <TableCell className="w-[50%] pl-5">
                <div className="flex items-center gap-2">
                  <span>{member.password}</span>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center justify-end gap-2">
        <Button
          color="secondary"
          size="md"
          variant="outlined"
          type="button"
          onClick={inviteMore}
        >
          Invite more
        </Button>
        <Button color="primary" size="md" type="button" onClick={close}>
          Close
        </Button>
      </div>
    </div>
  );
}
export function InviteOrgMemberModal(props: {
  changePlanTier?: ChangePaymentPlanTierSchema;
  onInviteSuccess?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [basicAuthMembers, setBasicAuthMembers] = useState<
    IdentitySchema[] | null
  >(null);

  const orgConfig = useCurrentOrganization();
  let maxSeats = orgConfig.data?.config.max_identities ?? 1;
  if (typeof maxSeats !== 'number') maxSeats = parseInt(maxSeats);
  const activeMembersResp = useMembers(
    MemberStatus.ACTIVE,
    MemberGroupType.ORGANIZATION
  );
  const pendingMembersResp = useMembers(
    MemberStatus.PENDING,
    MemberGroupType.ORGANIZATION
  );

  const memberCount = activeMembersResp.total + pendingMembersResp.total;

  const canAddSeats = orgConfig.data?.config?.can_add_seats;

  const disableInvite = memberCount >= maxSeats;
  const disableInviteSsoOnly = orgConfig.data?.sso_only;

  if (orgConfig.isLoading) return null;

  const seatsLeft = maxSeats - memberCount;
  const inviteDesc = `You can invite up to ${seatsLeft} more member${
    seatsLeft > 1 ? 's' : ''
  }`;

  const handleClose = () => {
    setOpen(false);
    setBasicAuthMembers(null);
  };
  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalDialog
          variant="outlined"
          sx={{ minWidth: 550, maxHeight: '90%', overflowY: 'auto' }}
        >
          <ModalClose size="lg" />
          <div className="flex w-full flex-col px-4">
            <h2 className="text-lg font-semibold">Invite members</h2>
            <h4 className="mb-4 text-sm font-normal text-tertiary">
              {inviteDesc}
            </h4>
            {basicAuthMembers ? (
              <BasicAuthInviteSuccess
                members={basicAuthMembers}
                close={handleClose}
                inviteMore={() => setBasicAuthMembers(null)}
              />
            ) : (
              <OrgMemberForm
                memberCount={memberCount}
                onComplete={(members) => {
                  if (mixedLoginMethods.basic) {
                    setBasicAuthMembers(members);
                  } else {
                    setOpen(false);
                  }
                  props.onInviteSuccess?.();
                }}
                changePlanTier={props.changePlanTier}
              />
            )}
          </div>
        </ModalDialog>
      </Modal>

      {canAddSeats && (
        <Tooltip
          title={
            (disableInvite &&
              'You have reached the maximum number of members for this organization.') ||
            (disableInviteSsoOnly &&
              'Your organization is SSO Only, so membership is managed strictly in your identity provider.')
          }
        >
          <span>
            <Button
              size="md"
              color="primary"
              leftDecorator={InviteUserIcon}
              disabled={disableInvite || disableInviteSsoOnly}
              onClick={() => setOpen(true)}
            >
              Invite
            </Button>
          </span>
        </Tooltip>
      )}
    </>
  );
}
